import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal";
import CloseIcon from "../../../assets/icons/close.svg";
import { App } from "../../../routes/Constants";
import QuitModal from "../../common/Modal/QuitModal";
import PostCreatePAFReview from "../../common/ViewPAF/components/PostCreatePAFReview";
import APIVariables, {
  buildHcpBrochurePath,
} from "../../../services/api-variables";
import { errorMessages } from "../../../constants/errorMessages.constants";
import { typeOfPAF } from "../../../constants/typeOfPAF";
import Loader from "../../common/Loader/newLoader";
import {
  formatCapsulesPerCycleIncompleteTitle,
  formatCapsulesPerWeeksIncompleteTitle,
  formatOutsidePermittedQuantityTitle,
  handleCatch,
  isObject,
  removeEmptyValueAndSelectLabel,
} from "../../../shared/helpers";
import {
  isMaxCapsulesExceededPatientInitiationWeeksSchedule,
  isMaxWeeksExceededPatientInitiation,
} from "../../../shared/pafComplianceChecks";
import ContinueConfirmationProvidedModal from "../PAFRenew/components/ContinueConfirmationProvidedModal";
import {
  ConfirmIdentity,
  DigitalPAF,
  DrugToPrescribe,
  NonComplaintPAF,
  PatientCategory,
  SelectTIF,
} from "./components";
import { PatientInitiationTopButtons } from "./components/PatientInitiationTopButtons";
import useRoles from "../../../hooks/useRoles";
import Axios from "../../../services/Axios";
import { useRegisterPatientContinuationResets } from "./hooks/useRegisterPatientContinuationResets";
import { patientInitiationRegisterModalStyles } from "./utils/patientInitiationModalStyles";
import {
  buildPrescriberCheckboxInitial,
  buildRiskCheckboxInitial,
} from "./utils/prescriptionCheckboxMaps";
import { REGISTER_PATIENT_INITIAL_STATE } from "./utils/registerPatientConstants";
import { buildPatientInitiationCreatePayload } from "./utils/buildPatientInitiationCreatePayload";
import { printPatientInitiationPaf } from "./utils/printPatientInitiationPaf";
import { evaluatePatientInitiationStep5 } from "./utils/PatientInitiationStep5Guard";
import { validatePatientInitiationFields } from "./utils/patientInitiationValidateFields";
import BrochureWithText from "../Brochure/BrochureWithText";

const PatientInitiation = () => {
  const [resId, setResId] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [personalDetails, setPersonalDetails] = useState({});
  const [isSubmittedPresciberPAF, setIsSubmittedPrescriberPAF] =
    useState(false);
  const [disableCompleteButton, setDisableCompleteButton] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openModalNonComplaint, setOpenModalNonComplaint] = useState({
    show: false,
    step: 1,
  });
  const [validPassword, setValidPassword] = useState("");
  const [errorMessageFromServer, setErrorMessageFromServer] = useState([]);
  const navigate = useNavigate();
  const [registerModalIsOpen, setIsOpenRegister] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [state, setState] = useState(1);
  const [prescriptionField, setPrescriptionField] = useState([]);
  const [registerPatient, setRegisterPatient] = useState(() => ({
    ...REGISTER_PATIENT_INITIAL_STATE,
  }));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isPrescriber, isPharmacy, isConnectedRegistrar, storageData } =
    useRoles();
  const [brochures, setBrochures] = useState([]);
  const [profileData, setProfileData] = useState();
  const drugToPrescribe = !!profileData && profileData?.products;

  const treatmentFormText =
    !!drugToPrescribe &&
    drugToPrescribe?.find(
      (element) => element.product_id === registerPatient?.product
    );

  const treatmentFormLabel =
    treatmentFormText?.treatment_form_text || "treatment form";

  const scheduleDisplayType = prescriptionField?.schedule_display_type;

  const getProfileData = async () => {
    try {
      const response = await Axios.get(APIVariables.USERS_PROFILE);

      const { status, data } = response;

      if (status === 200) {
        setProfileData(data);
      }
    } catch (error) {
      handleCatch(error);
    }
  };

  // Get confirmations product from BE
  const getPrescriptionField = async () => {
    try {
      const data = await Axios.get(
        APIVariables.PRESCRIPTION_FIELDS +
        registerPatient.product +
        `/${registerPatient.type}`
      );
      setPrescriptionField(data.data);
    } catch (error) {
      handleCatch(error);
    }
  };

  const [hcp, setHcp] = useState();

  useRegisterPatientContinuationResets(registerPatient, setRegisterPatient);

  const getHCP = async () => {
    try {
      const data = await Axios.get(
        buildHcpBrochurePath(registerPatient.product)
      );

      setHcp(data.data);
    } catch (error) {
      handleCatch(error);
    }
  };

  useEffect(() => {
    state === 5 && getPrescriptionField() && getHCP();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    state === 2 && getProfileData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    loadInitial();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setRegisterPatient((prev) => {
      if (!prev) return prev;
      const hasDate =
        prev.pregnancy_test != null &&
        String(prev.pregnancy_test).trim() !== "";
      const { continueWithPregnancyTestDateOutdated, continueWithPregnancyTestDateIncomplete } =
        prev;

      if (hasDate && (continueWithPregnancyTestDateOutdated || continueWithPregnancyTestDateIncomplete)) {
        return {
          ...prev,
          continueWithPregnancyTestDateOutdated: false,
          continueWithPregnancyTestDateIncomplete: false,
        };
      }
      if (!hasDate && continueWithPregnancyTestDateOutdated) {
        return { ...prev, continueWithPregnancyTestDateOutdated: false };
      }
      return prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerPatient?.pregnancy_test]);

  useEffect(() => {
    if (registerPatient?.indication) {
      setRegisterPatient({
        ...registerPatient,
        continueOffLabel: false,
        continueIndicationIncomplete: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerPatient?.indication]);

  // Changing cycles invalidates cycle- and max-weeks acknowledgment flags.
  // Functional updates avoid stale closures re-applying old flag values.
  useEffect(() => {
    if (registerPatient?.number_cycles) {
      setRegisterPatient((prev) => ({
        ...prev,
        continueNumberOfCyclesIncomplete: false,
        continueMaximumNumberOfCyclesExceeded: false,
        continueMaximumNumberOfWeeksExceeded: false,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerPatient?.number_cycles]);

  useEffect(() => {
    if (registerPatient?.number_weeks || registerPatient?.other) {
      setRegisterPatient({
        ...registerPatient,
        continueMaximumNumberOfWeeksExceeded: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerPatient?.number_weeks, registerPatient?.other]);

  useEffect(() => {
    if (registerPatient?.total_capsules) {
      setRegisterPatient({
        ...registerPatient,
        continueMaximumNumberOfCapsulesExceeded: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerPatient?.total_capsules]);

  useEffect(() => {
    if (registerPatient.number_weeks) {
      setRegisterPatient({
        ...registerPatient,
        continueNumberOfWeeksIncomplete: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerPatient.number_weeks]);

  useEffect(() => {
    if (registerPatient?.patient_dob) {
      setRegisterPatient({
        ...registerPatient,
        continueWithDateOfBirthUnderEighteen: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerPatient?.patient_dob]);

  useEffect(() => {
    if (state === 3) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state === 3]);

  async function fetchData() {
    try {
      const brochures = await Axios.get(
        APIVariables.PRESCRIPTIONS_TIF + registerPatient?.product
      );

      setBrochures(brochures.data.tifs);
    } catch (error) {
      handleCatch(error);
    }
  }

  function loadInitial() {
    const composedUserDetails = {
      ...storageData,
      hospital_name: storageData?.hospital?.value,
      hospital_address: storageData?.hospital?.address,
    };

    setPersonalDetails(composedUserDetails);
  }

  const goToProfile = () => {
    navigate(`${App.PROFILE_DEFAULT}` + personalDetails.registration_number);
  };

  const isNotSelectedAnyCapsulePerCycle = () => {
    if (registerPatient?.capsules?.length > 0) {
      const result = registerPatient?.capsules?.some(
        (capsule) => Boolean(capsule.label) || Boolean(capsule.other)
      );
      return result;
    } else {
      return false;
    }
  };

  // const isNotSelectedAnyWeeksOrCycles = () => {
  //   const isNullOrEmpty = (item) => item === null || item === undefined;

  //   const isNone = (item) => item?.id === null && item?.label === "None";

  //   const checkNumberWeeks = () => {
  //     if (
  //       isNullOrEmpty(registerPatient?.number_weeks) ||
  //       isNone(registerPatient?.number_weeks)
  //     ) {
  //       return true;
  //     }
  //     if (
  //       registerPatient?.number_weeks?.id === 0 &&
  //       registerPatient?.number_weeks?.label === "Other (Please State)"
  //     ) {
  //       return isNullOrEmpty(registerPatient?.other);
  //     }
  //     return false;
  //   };

  //   const checkWeeksPerCycle = () =>
  //     isNullOrEmpty(registerPatient?.weeks_per_cycle) ||
  //     isNone(registerPatient?.weeks_per_cycle);

  //   return checkNumberWeeks() || checkWeeksPerCycle();
  // };

  const submitPAFToServer = async () => {
    const {
      continueOffLabel,
      continueIndicationIncomplete,
      continueWithoutConfirmation,
      continueWithPregnancyTestDateOutdated,
      continueWithPregnancyTestDateIncomplete,
      continueMaximumNumberOfCyclesExceeded,
      continueMaximumNumberOfWeeksExceeded,
      continueMaximumNumberOfCapsulesExceeded,
      nonComplaintValidationPassed,
      continueWithDateOfBirthUnderEighteen,
      continueCapsulePerCycleIncomplete,
      continueCapsulePerWeekIncomplete,
      continueNumberOfCyclesIncomplete,
      continueNumberOfWeeksIncomplete,
    } = registerPatient;

    const maxWeeksStillExceeded = isMaxWeeksExceededPatientInitiation(
      registerPatient,
      prescriptionField
    );
    // User clicked Continue on the max-weeks warning, but only treat as non-compliant
    // if totals are still over the limit (ignore leftover flags after they fix the numbers).
    const continueMaxWeeksWithStaleAck =
      continueMaximumNumberOfWeeksExceeded && maxWeeksStillExceeded;

    const maxCapsStillExceeded =
      isMaxCapsulesExceededPatientInitiationWeeksSchedule(
        registerPatient,
        prescriptionField
      );
    const continueMaxCapsWithStaleAck =
      continueMaximumNumberOfCapsulesExceeded && maxCapsStillExceeded;

    if (
      continueOffLabel ||
      continueIndicationIncomplete ||
      continueWithoutConfirmation ||
      continueWithPregnancyTestDateOutdated ||
      continueWithPregnancyTestDateIncomplete ||
      continueMaximumNumberOfCyclesExceeded ||
      continueMaxWeeksWithStaleAck ||
      continueMaxCapsWithStaleAck ||
      continueWithDateOfBirthUnderEighteen ||
      continueCapsulePerCycleIncomplete ||
      continueCapsulePerWeekIncomplete ||
      continueNumberOfCyclesIncomplete ||
      continueNumberOfWeeksIncomplete
    ) {
      if (!nonComplaintValidationPassed) {
        setOpenModalNonComplaint({ show: true, step: 1 });
      } else {
        setIsOpenRegister(true);
      }
    } else {
      setIsOpenRegister(true);
    }

    setDisableCompleteButton(false);
  };

  const nextPage = async () => {
    setShowErrors(false);

    if (state === 6) {
      setIsSubmittedPrescriberPAF(false);
      if (!areAllFieldsValid) {
        setShowErrors(true);
        return;
      } else {
        setIsOpenRegister(true);
      }
    }

    if (state === 5) {
      const step5 = evaluatePatientInitiationStep5({
        areAllFieldsValid,
        registerPatient,
        prescriptionField,
        scheduleDisplayType,
        hcp,
        confirmation_risk_patient,
      });
      if (step5.outcome === "SHOW_ERRORS") {
        setShowErrors(true);
        return;
      }
      if (step5.outcome === "MODAL") {
        setOpenModal(true);
        setErrorMessage((prevState) => ({
          ...prevState,
          ...step5.errorMessage,
        }));
        return;
      }

      if (
        confirmation_risk_patient !== true &&
        registerPatient.continueWithoutConfirmation !== true &&
        registerPatient.type !== typeOfPAF.WOMANOFNCP &&
        Boolean(prescriptionField.prescription_confirmation_risks.length)
      ) {
        setOpenModal(true);
        setErrorMessage((prevState) => ({
          ...prevState,
          type: "error",
          modalState: 1,
          hasSecondState: false,
          title: errorMessages.CONFIRMATION_NOT_PROVIDED_TITLE,
          description: (
            <BrochureWithText
              type={"CONFIRMATION_NOT_PROVIDED_DESCRIPTION"}
              hcp={hcp}
              confirmationRiskLength={
                prescriptionField?.prescription_confirmation_risks?.length
              }
            />
          ),
          buttonText: "Continue",
        }));
        return;
      }

      if (
        registerPatient?.continueOffLabel !== true &&
        registerPatient?.indication?.is_indication_other === true
      ) {
        setOpenModal(true);
        setErrorMessage((prevState) => ({
          ...prevState,
          type: "error",
          modalState: 1,
          hasSecondState: false,
          title: "Continue with off-label use",
          description: (
            <BrochureWithText type={"CONTINUE_OFF_LABEL"} hcp={hcp} />
          ),
          buttonText: "Continue",
        }));
        return;
      }

      if (
        registerPatient?.continueIndicationIncomplete !== true &&
        (registerPatient?.indication?.label === undefined ||
          registerPatient?.indication?.label === null ||
          registerPatient?.indication?.label === "None")
      ) {
        setOpenModal(true);
        setErrorMessage((prevState) => ({
          ...prevState,
          type: "error",
          modalState: 1,
          hasSecondState: false,
          title: errorMessages.INDICATION_INCOMPLETE_TITLE,
          description: (
            <BrochureWithText
              type={"CONTINUE_INDICATION_INCOMPLETE"}
              hcp={hcp}
            />
          ),
          buttonText: "Continue",
        }));
        return;
      }

      if (
        (scheduleDisplayType === "cycle" ||
          scheduleDisplayType === "weeks_per_cycle") &&
        registerPatient?.continueCapsulePerCycleIncomplete !== true &&
        !isNotSelectedAnyCapsulePerCycle()
      ) {
        setOpenModal(true);
        setErrorMessage((prevState) => ({
          ...prevState,
          type: "error",
          modalState: 1,
          hasSecondState: false,
          code: errorMessages.CAPSULES_PER_CYCLE_INCOMPLETE_CODE,
          title: formatCapsulesPerCycleIncompleteTitle(
            registerPatient?.unit_label ?? registerPatient?.product_unit_label
          ),
          description: (
            <BrochureWithText
              type={"CONTINUE_CAPSULE_PER_CYCLE_INCOMPLETE"}
              hcp={hcp}
              scheduleDisplayType={prescriptionField?.schedule_display_type}
              unitLabel={
                registerPatient?.unit_label ?? registerPatient?.product_unit_label
              }
            />
          ),
          buttonText: "Continue",
        }));
        return;
      }

      if (
        scheduleDisplayType === "weeks" &&
        registerPatient?.continueCapsulePerWeekIncomplete !== true &&
        !isNotSelectedAnyCapsulePerCycle()
      ) {
        setOpenModal(true);
        setErrorMessage((prevState) => ({
          ...prevState,
          type: "error",
          modalState: 1,
          hasSecondState: false,
          code: errorMessages.CAPSULES_PER_WEEKS_INCOMPLETE_CODE,
          title: formatCapsulesPerWeeksIncompleteTitle(
            registerPatient?.unit_label ?? registerPatient?.product_unit_label
          ),
          description: (
            <BrochureWithText
              type={"CONTINUE_CAPSULE_PER_CYCLE_INCOMPLETE"}
              hcp={hcp}
              scheduleDisplayType={prescriptionField?.schedule_display_type}
              unitLabel={
                registerPatient?.unit_label ?? registerPatient?.product_unit_label
              }
            />
          ),
          buttonText: "Continue",
        }));
        return;
      }

      if (
        (scheduleDisplayType === "cycle" ||
          scheduleDisplayType === "weeks_per_cycle") &&
        registerPatient?.continueNumberOfCyclesIncomplete !== true &&
        (registerPatient?.number_cycles?.value === 0 ||
          !registerPatient?.number_cycles ||
          !registerPatient?.number_cycles.id)
      ) {
        setOpenModal(true);
        setErrorMessage((prevState) => ({
          ...prevState,
          type: "error",
          modalState: 1,
          hasSecondState: false,
          title: errorMessages.NUMBER_OF_CYCLES_INCOMPLETE,
          description: (
            <BrochureWithText
              type={"CONTINUE_NUMBER_OF_CYCLES_INCOMPLETE"}
              hcp={hcp}
            />
          ),
          buttonText: "Continue",
        }));
        return;
      }

      if (
        registerPatient?.continueNumberOfWeeksIncomplete !== true &&
        ((scheduleDisplayType === "weeks_per_cycle" &&
          (!registerPatient?.weeks_per_cycle ||
            registerPatient.weeks_per_cycle.label === "None")) ||
          (scheduleDisplayType === "weeks" &&
            (!registerPatient?.number_weeks ||
              registerPatient?.number_weeks.label === "None")))
      ) {
        setOpenModal(true);
        setErrorMessage((prevState) => ({
          ...prevState,
          type: "error",
          modalState: 1,
          hasSecondState: false,
          title: errorMessages.NUMBER_OF_WEEKS_INCOMPLETE,
          description: (
            <BrochureWithText
              type={"CONTINUE_NUMBER_OF_WEEKS_INCOMPLETE"}
              hcp={hcp}
            />
          ),
          buttonText: "Continue",
        }));
        return;
      }

      const isNumberOfCylesExceeded =
        checkIfNumberOfCyclesExceeded(registerPatient);

      const isPureWeeksSchedule = scheduleDisplayType === "weeks";

      if (
        !isPureWeeksSchedule &&
        (scheduleDisplayType === "cycle" ||
          scheduleDisplayType === "weeks_per_cycle") &&
        registerPatient.continueMaximumNumberOfCyclesExceeded !== true &&
        isNumberOfCylesExceeded
      ) {
        setOpenModal(true);
        setErrorMessage((prevState) => ({
          ...prevState,
          type: "error",
          modalState: 1,
          hasSecondState: false,
          title: errorMessages.MAXIM_NUMBER_OF_CYLES_EXCEEDED_TITLE,
          description:
            registerPatient.type === typeOfPAF.MALE ? (
              <BrochureWithText type="CYCLES_EXCEEDED_MALE" hcp={hcp} />
            ) : registerPatient.type === typeOfPAF.WOMANOFNCP ? (
              <BrochureWithText type="CYCLES_EXCEEDED_WOMANOFNCP" hcp={hcp} />
            ) : (
              <BrochureWithText type={"CYCLES_EXCEEDED_WCBP"} hcp={hcp} />
            ),
          buttonText: "Continue",
        }));
        return;
      }

      if (
        scheduleDisplayType === "weeks" &&
        registerPatient.continueMaximumNumberOfCapsulesExceeded !== true &&
        (typeOfPAF.WOMANOFCP === registerPatient.type
          ? !!prescriptionField.max_capsules_wcbp
          : !!prescriptionField.max_capsules_m_or_wncbp) &&
        (registerPatient.total_capsules !== 0
          ? registerPatient.total_capsules
          : registerPatient.capsule_strength) >
        (typeOfPAF.WOMANOFCP === registerPatient.type
          ? prescriptionField.max_capsules_wcbp
          : prescriptionField.max_capsules_m_or_wncbp)
      ) {
        setOpenModal(true);
        setErrorMessage((prevState) => ({
          ...prevState,
          type: "error",
          modalState: 1,
          hasSecondState: false,
          title: formatOutsidePermittedQuantityTitle(
            registerPatient?.unit_label || 'capsule'
          ),
          code: "MAX_CAPSULES_EXCEEDED",
          description: (
            <BrochureWithText
              type={"CAPSULES_EXCEEDED"}
              typeOfPAF={typeOfPAF}
              registerPatientType={registerPatient.type}
              maxCapsulesOrWeeks={prescriptionField}
              unitLabel={registerPatient?.unit_label}
              hcp={hcp}
            />
          ),
          buttonText: "Continue",
        }));
        return;
      }

      {
        const isWeekSchedule =
          scheduleDisplayType === "weeks" ||
          scheduleDisplayType === "weeks_per_cycle";

        const weeksValue =
          scheduleDisplayType === "weeks_per_cycle"
            ? registerPatient?.total_weeks_supply
            : registerPatient?.other
              ? registerPatient?.other
              : registerPatient?.number_weeks?.id;

        const maxWeeks =
          typeOfPAF.WOMANOFCP === registerPatient.type
            ? prescriptionField.max_weeks_wcbp
            : prescriptionField.max_weeks_m_or_wncbp;

        if (
          isWeekSchedule &&
          maxWeeks &&
          weeksValue > maxWeeks
        ) {
          // show the modal only the first time
          if (registerPatient.continueMaximumNumberOfWeeksExceeded !== true) {
            setOpenModal(true);
            setErrorMessage((prevState) => ({
              ...prevState,
              type: "error",
              modalState: 1,
              hasSecondState: false,
              title: "Maximum number of weeks exceeded",
              code: "MAX_WEEKS_EXCEEDED",
              description: (
                <BrochureWithText
                  type={"WEEKS_EXCEEDED"}
                  typeOfPAF={typeOfPAF}
                  registerPatientType={registerPatient.type}
                  maxCapsulesOrWeeks={prescriptionField}
                  hcp={hcp}
                />
              ),
              buttonText: "Continue",
            }));
            return;
          }
          // already acknowledged -> fallthrough and continue normally (do not show second modal)
        }
      }

      setState(state + 1);
    }

    if (state === 1 || state === 4 || state === 2) {
      setState(state + 1);
    }

    if (state === 3 && (isPrescriber || isConnectedRegistrar)) {
      if (!!registerPatient.confirmation_other1) {
        setShowErrors(false);
        setState(state + 1);
      } else {
        setShowErrors(true);
        return;
      }
    }
  };

  const previous = () => {
    setState(state - 1);

    if (state === 5) {
      setRegisterPatient((prevState) => ({
        ...prevState,
        number_cycles: null,
        number_weeks: null,
        total_capsules: null,
        capsules: null,
        strengths: null,
        capsule_strength: null,
        other: null,
        total_weeks_supply: null,
        indication: null,
        other_indication: null,
        weeks_per_cycle: null,
      }));
    }

    if (state === 6) {
      setCheckedItemsPrescriber(checkedItemsPrescriber);
      setCheckedItems(checkedItems);

      setRegisterPatient((prevState) => ({
        ...prevState,
        confirmation_risk_patient: registerPatient.confirmation_risk_patient,
        continueWithoutConfirmation:
          registerPatient.continueWithoutConfirmation,
        confirmation_risk_prescriber:
          registerPatient.confirmation_risk_prescriber,
      }));
    }
  };

  const updateRegisterPatient = (field, value) => {
    setRegisterPatient((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const selectTypeOfPatient = (type) => {
    if (type === typeOfPAF.WOMANOFNCP) {
      setRegisterPatient((prevState) => ({
        ...prevState,
        type: type,
        continueWithoutConfirmation: false,
      }));
    } else {
      setRegisterPatient((prevState) => ({
        ...prevState,
        type: type,
      }));
    }
  };

  const selectTypeOfDrug = (type, id, unit_label) => {
    setRegisterPatient((prevState) => ({
      ...prevState,
      drug: type,
      product: id,
      unit_label: unit_label,
    }));
  };

  const handleChange = (e) => {
    let { name, value, maxLength } = e.target;
    if (name?.toLowerCase()?.includes("capsule_cycle_other")) {
      let parsedValue = parseInt(value);
      value = parsedValue ? parsedValue.toString() : value;
      if (+value <= 0) {
        value = "1";
      } else if (+value > 200) {
        value = "200";
      }
    }

    const shouldSetValue = +value.length <= maxLength;

    let filterValue = value;

    const nameRegex = /[^a-zA-Z]/g;

    if (name === "patient_initials") {
      if (value === "" || nameRegex.test(e.target.value)) {
        filterValue = filterValue.replace(nameRegex, "");
      }
    }

    if (name === "patient_initials") {
      filterValue = filterValue?.toUpperCase();
    }

    const formCopy = {
      ...registerPatient,
      [name]: filterValue,
    };

    if (maxLength === -1) {
      setRegisterPatient(formCopy);
    } else {
      if (shouldSetValue) {
        setRegisterPatient(formCopy);
      }
    }

    setTotalCapsules(formCopy);
  };

  // Initialized prescription_confirmation_risks and prescriber_confirmations with checkbox-prescriber-${item.id} and risk-checkbox-${item.id}

  const initialValue = buildPrescriberCheckboxInitial(prescriptionField);
  const initialValueRisk = buildRiskCheckboxInitial(prescriptionField);

  // Set the checkedItems and checkedItemsPrescriber with initial values (initialValue and initialValueRisk)
  useEffect(() => {
    if (
      !!prescriptionField?.prescriber_confirmations &&
      !!initialValue &&
      Object.keys(checkedItemsPrescriber).length === 0
    ) {
      setCheckedItemsPrescriber(initialValue);
    }
    if (
      !!prescriptionField?.prescription_confirmation_risks &&
      !!initialValueRisk &&
      Object.keys(checkedItems).length === 0
    ) {
      setCheckedItems(initialValueRisk);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    prescriptionField?.prescriber_confirmations,
    prescriptionField?.prescription_confirmation_risks,
  ]);

  const [checkedItems, setCheckedItems] = useState(initialValueRisk || {});
  const [checkedItemsPrescriber, setCheckedItemsPrescriber] = useState(
    initialValue || {}
  );

  const [allChecked, setAllChecked] = useState(false);
  const [allCheckedPrescriber, setAllCheckedPrescriber] = useState(false);

  useEffect(() => {
    // Calculate allChecked based on checkedItems
    const calculatedAllChecked =
      Object.keys(checkedItems).length > 0 &&
      Object.values(checkedItems).every(Boolean);

    const calculatedAllCheckedPrescriber =
      Object.keys(checkedItemsPrescriber).length > 0 &&
      Object.values(checkedItemsPrescriber).every(Boolean);

    // Update the allChecked state variable
    setAllChecked(calculatedAllChecked);
    setAllCheckedPrescriber(calculatedAllCheckedPrescriber);
  }, [checkedItems, checkedItemsPrescriber]);

  useEffect(() => {
    if (checkedItems && allChecked) {
      setRegisterPatient((prevState) => ({
        ...prevState,
        continueWithoutConfirmation: false,
        confirmation_risk_patient: allChecked,
        confirmation_risk_prescriber: allCheckedPrescriber,
      }));
    }
    if (checkedItems) {
      setRegisterPatient((prevState) => ({
        ...prevState,
        confirmation_risk_patient: allChecked,
        confirmation_risk_prescriber: allCheckedPrescriber,
      }));
    }
  }, [allChecked, allCheckedPrescriber, checkedItems]);

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;

    if (name === "confirmation_other1") {
      if (checked) {
        setShowErrors(false);
      }
      setRegisterPatient((prevState) => ({
        ...prevState,
        confirmation_other1: checked,
      }));
    }

    if (name.includes("checkbox-prescriber")) {
      setCheckedItemsPrescriber({ ...checkedItemsPrescriber, [name]: checked });
    } else if (name.includes("risk-checkbox")) {
      setCheckedItems({ ...checkedItems, [name]: checked });
    }
  };

  const printPAF = () => printPatientInitiationPaf(resId);

  const redirectToOrderPage = () => {
    if (isPharmacy) {
      navigate(App.ORDER_DEFAULT + resId);
    } else {
      navigate(App.VIEW_PATIENTS);
    }
  };

  const closeModalRegisterModal = () => {
    setIsOpenRegister(false);
  };

  const closeNonComplaint = () => {
    setOpenModalNonComplaint({ show: false, step: 1 });
  };

  const submitNonComplaint = async () => {
    if (openModalNonComplaint.step === 1) {
      setOpenModalNonComplaint({ show: true, step: 2 });
    }

    if (openModalNonComplaint.step === 2) {
      const res = await Axios.post(APIVariables.USERS_PASSWORD_VALIDATE, {
        password: validPassword,
      }).catch(function (error) {
        handleCatch(error);

        const errors = error?.response?.data;
        if (typeof errors === "string") {
          setErrorMessageFromServer([errorMessages.INTERNAL_SERVER_ERROR]);
        } else {
          const errorsArray = [];
          if (!!errors) {
            Object.keys(errors).forEach((key) => {
              if (isObject(errors[key])) {
                Object.keys(errors[key]).forEach((secondKey) => {
                  errorsArray.push(errors[key][secondKey][0]);
                });
              } else {
                errorsArray.push(errors[key]);
              }
            });
            setErrorMessageFromServer(errorsArray);
          } else {
            setErrorMessageFromServer([error.message]);
          }
        }
      });

      if (res?.status === 200) {
        closeNonComplaint();
        setRegisterPatient({
          ...registerPatient,
          nonComplaintValidationPassed: true,
        });
      }
    }
  };

  const disableNextButton = () => {
    if (isPharmacy) {
    } else {
      if (state === 4) {
        return !registerPatient.type;
      }
      if (state === 2) {
        return !registerPatient.drug;
      }
    }
  };

  const [quitModalIsOpen, setIsOpenQuit] = useState(false);

  function closeModalQuit() {
    setIsOpenQuit(false);
  }

  function quitModal() {
    navigate(App.DASHBOARD);
  }

  function closeForm() {
    setIsOpenQuit(true);
  }

  const handleChangeValidPassword = (event) => {
    setValidPassword(event.target.value);
  };

  const checkIfNumberOfCyclesExceeded = (form) => {
    const typeOfFormVar = form?.type;
    const nrCycles = form?.number_cycles?.id;

    if (typeOfFormVar === typeOfPAF.MALE || typeOfFormVar === typeOfPAF.WOMANOFNCP) {
      return nrCycles > 3;
    }
    if (typeOfFormVar === typeOfPAF.WOMANOFCP) {
      return nrCycles > 1;
    }
    return false;
  };

  const submitForm = async () => {
    return new Promise(async (response) => {
      setErrorMessage("");
      setIsSubmitting(true);

      const API = APIVariables.PRESCRIPTIONS_CREATE_POST;
      const copyOfRegisterPatient = buildPatientInitiationCreatePayload({
        registerPatient,
        checkedItems,
        checkedItemsPrescriber,
        storageData,
      });

      const res = await Axios.post(API, copyOfRegisterPatient).catch(function (
        error
      ) {
        setIsSubmitting(false);
        handleCatch(error);

        const errors = error?.response?.data;
        if (!!errors) {
          const errorsArray = [];
          if (typeof errors === "string") {
            errorsArray.push(errorMessages.INTERNAL_SERVER_ERROR);
          } else {
            Object?.keys(errors).forEach((key) => {
              errorsArray.push(key + " - " + errors[key]);
            });
          }

          setErrorMessage(errorsArray);
          return response(errorsArray);
        }
      });

      if (res?.status === 200) {
        setResId(res.data.patient_id);
        setIsSubmitting(false);
        setIsSubmittedPrescriberPAF(true);
        setIsOpenRegister(true);
        closeModalRegisterModal();

        setRegisterPatient({ ...registerPatient, id: res.data.patient_id });
        return response(true);
      }
      setIsSubmitting(false);
    });
  };

  const setTotalCapsules = (copiedRegisterPatient) => {
    if (prescriptionField?.schedule_display_type === "weeks_per_cycle") {
      const TotalweeksPerCycle =
        prescriptionField?.schedule_display_type === "weeks_per_cycle" &&
        copiedRegisterPatient?.number_cycles?.id *
        (copiedRegisterPatient.weeks_per_cycle?.id !== 0
          ? copiedRegisterPatient.weeks_per_cycle?.id
          : copiedRegisterPatient?.other);

      setRegisterPatient((prevState) => ({
        ...prevState,

        total_weeks_supply:
          prescriptionField.schedule_display_type === "weeks_per_cycle" &&
          (TotalweeksPerCycle || ""),
      }));
    }

    if (!!copiedRegisterPatient.capsules) {
      // Calculate the sum of the values using the reduce method
      const capsuleValues = Object.values(copiedRegisterPatient.capsules);

      const sumOfValues = capsuleValues
        ?.map((element) =>
          element.label === 0 ? element.other : element.label
        )
        ?.reduce((accumulator, currentValue) => accumulator + currentValue, 0);

      const numberOfCyles =
        copiedRegisterPatient?.number_weeks?.id === 0
          ? copiedRegisterPatient?.other
          : copiedRegisterPatient?.number_weeks?.id ||
          copiedRegisterPatient?.number_cycles?.id;
      const totalCapsules = +sumOfValues * (numberOfCyles || 0);

      setRegisterPatient((prevState) => ({
        ...prevState,
        capsule_strength: sumOfValues,
        total_capsules: !!copiedRegisterPatient?.number_weeks
          ? Math.ceil(totalCapsules / 4)
          : totalCapsules,
      }));
    }
  };

  const {
    patient_initials,
    patient_dob,
    indication,
    indication_other,
    clinical_trial,
    number_cycles,
    // Pharmacy
    prescriber_name,
    hospital_name_and_address,
    date_paper_paf,
    confirmation_other1,
    confirmation_other2,
    confirmation_risk_prescriber,
    confirmation_risk_patient,
  } = registerPatient;

  const fields = validatePatientInitiationFields(
    {
      patient_initials,
      patient_dob,
      indication,
      indication_other,
      clinical_trial,
      number_cycles,
      prescriber_name,
      hospital_name_and_address,
      date_paper_paf,
      confirmation_other1,
      confirmation_other2,
      confirmation_risk_prescriber,
      confirmation_risk_patient,
    },
    {
      isPrescriber,
      isConnectedRegistrar,
      isPharmacy,
      prescriptionField,
      registerPatientType: registerPatient.type,
      allCheckedPrescriber,
    }
  );

  const areAllFieldsValid = Object.values(fields).every((field) => field.isValid);

  const getUserInfo = () => {
    const {
      id,
      patient_initials,
      type,
      patient_dob,
      hospital_name_and_address,
    } = registerPatient;

    const userInfo = {
      id: id || "Awaiting Completion",
      patient_initials,
      gender: type === typeOfPAF.MALE ? "Male" : "Female",
      dob: patient_dob,
      hospital: isPharmacy
        ? hospital_name_and_address
        : storageData?.hospital?.value,
    };
    return userInfo;
  };

  const deletePropertyValue = (propName) => {
    const copyRegisterPatient = { ...registerPatient };
    delete copyRegisterPatient[propName];

    setRegisterPatient(copyRegisterPatient);
  };

  const handleCapsules = (capsuleCycleSelected, name, capsuleStrength) => {
    if (name === "clinical_trial" || name === "indication_other") {
      const formCopy = {
        ...registerPatient,

        [name]: capsuleCycleSelected,
      };
      setRegisterPatient(formCopy);
    }

    if (
      typeof capsuleCycleSelected === "string" &&
      name === "capsule_per_cycle"
    ) {
      let parsedValue = parseInt(capsuleCycleSelected);
      capsuleCycleSelected = parsedValue
        ? parsedValue.toString()
        : capsuleCycleSelected;
      if (+capsuleCycleSelected <= 0) {
        capsuleCycleSelected = "1";
      } else if (+capsuleCycleSelected > 200) {
        capsuleCycleSelected = "200";
      }
      let maxLength = -1;
      const shouldSetValue = +capsuleStrength.length <= maxLength;

      const formCopy = {
        ...registerPatient,
        [capsuleCycleSelected]: capsuleCycleSelected,
      };

      if (maxLength === -1) {
        setRegisterPatient(formCopy);
      } else {
        if (shouldSetValue) {
          setRegisterPatient(formCopy);
        }
      }

      setTotalCapsules(formCopy);
    }
    if (name === "capsule_per_cycle") {
      const copiedRegisterPatient = { ...registerPatient };
      const valuee = capsuleStrength.id;

      if (!Array.isArray(copiedRegisterPatient.capsules)) {
        copiedRegisterPatient.capsules = [];
      }
      const existingIndex = copiedRegisterPatient.capsules.findIndex(
        (capsule) => capsule.id === valuee
      );

      if (existingIndex !== -1) {
        copiedRegisterPatient.capsules[existingIndex] = {
          id: valuee,
          label:
            capsuleCycleSelected.value === undefined
              ? 0
              : capsuleCycleSelected.value,
          other:
            capsuleCycleSelected.value === 0
              ? 1
              : typeof capsuleCycleSelected === "string" &&
              +capsuleCycleSelected,
          //   typeof capsuleCycleSelected === "string" && +capsuleCycleSelected,
          capsuleStrength: capsuleStrength,

          strengths: {
            [valuee]:
              capsuleCycleSelected.value === undefined
                ? +capsuleCycleSelected
                : capsuleCycleSelected.value,
          },
        };
      } else {
        copiedRegisterPatient.capsules.push({
          id: valuee,
          label: capsuleCycleSelected.value,
          capsuleStrength: capsuleStrength,
          other: capsuleCycleSelected.value === 0 ? 1 : false,

          strengths: {
            [valuee]:
              capsuleCycleSelected.value === undefined
                ? +capsuleCycleSelected
                : capsuleCycleSelected.value === 0
                  ? 1
                  : capsuleCycleSelected.value,
          },
        });
      }

      copiedRegisterPatient.strengths = {};

      // Iterate through capsules and merge strengths objects
      copiedRegisterPatient.capsules.forEach((capsule) => {
        const { strengths } = capsule;
        Object.assign(copiedRegisterPatient.strengths, strengths);
      });

      for (const key in copiedRegisterPatient.strengths) {
        if (copiedRegisterPatient.strengths[key] === null) {
          delete copiedRegisterPatient.strengths[key];
        }
      }

      copiedRegisterPatient.capsules = copiedRegisterPatient.capsules.filter(
        (obj) => obj.label !== null
      );

      copiedRegisterPatient.continueCapsulePerCycleIncomplete =
        capsuleCycleSelected.value === null && false;

      copiedRegisterPatient.continueCapsulePerWeekIncomplete =
        capsuleCycleSelected.value === null && false;

      copiedRegisterPatient.continueMaximumNumberOfCapsulesExceeded = false;

      setRegisterPatient(copiedRegisterPatient);
      setTotalCapsules(copiedRegisterPatient);
    }

    if (name === "number_cycles") {
      const formcopy = {
        ...registerPatient,
        [name]:
          capsuleCycleSelected?.id === 0 &&
            capsuleCycleSelected?.label === "None"
            ? null
            : capsuleCycleSelected,
        continueNumberOfCyclesIncomplete:
          capsuleCycleSelected.id === 0 && false,
        continueMaximumNumberOfCyclesExceeded: false,
        continueMaximumNumberOfWeeksExceeded: false,
        // TODO: de vazut la scheduleDisplayType === cycle sa nu fie probleme
        // other: null,
        // number_weeks: null,
      };

      setRegisterPatient(formcopy);
      setTotalCapsules(formcopy);
    }

    if (name === "number_weeks") {
      const formcopy = {
        ...registerPatient,
        [name]:
          capsuleCycleSelected.id === 0 && capsuleCycleSelected.label === "None"
            ? null
            : capsuleCycleSelected,
        continueNumberOfWeeksIncomplete: capsuleCycleSelected.id === 0 && false,
        continueMaximumNumberOfWeeksExceeded: false,
        other:
          capsuleCycleSelected.id === 0 &&
            capsuleCycleSelected.label === "Other (Please State)"
            ? 1
            : null,
        number_cycles: null,
      };

      setRegisterPatient(formcopy);
      setTotalCapsules(formcopy);
    }

    if (name === "number_weeks_other") {
      let parsedValue = parseInt(capsuleCycleSelected);

      capsuleCycleSelected = parsedValue
        ? parsedValue.toString()
        : capsuleCycleSelected;
      if (+capsuleCycleSelected <= 0) {
        capsuleCycleSelected = "1";
      } else if (+capsuleCycleSelected > 12) {
        capsuleCycleSelected = "12";
      }
      let maxLength = -1;
      const shouldSetValue = +capsuleCycleSelected.length <= maxLength;

      const formCopy = {
        ...registerPatient,
        other: capsuleCycleSelected,
        continueMaximumNumberOfWeeksExceeded: false,
      };

      if (maxLength === -1) {
        setRegisterPatient(formCopy);
      } else {
        if (shouldSetValue) {
          setRegisterPatient(formCopy);
        }
      }

      setTotalCapsules(formCopy);
    }

    if (name === "weeks_per_cycle") {
      const formcopy = {
        ...registerPatient,
        weeks_per_cycle:
          capsuleCycleSelected?.id === 0 &&
            capsuleCycleSelected?.label === "None"
            ? null
            : capsuleCycleSelected,

        other: capsuleCycleSelected?.id === 0 ? 1 : null,
        continueMaximumNumberOfWeeksExceeded: false,
        continueNumberOfWeeksIncomplete: capsuleCycleSelected.id === 0 && false,
      };

      setRegisterPatient(formcopy);
      setTotalCapsules(formcopy);
    }

    if (name === "indication") {
      if (capsuleCycleSelected.id === -1) {
        setRegisterPatient(() => ({
          ...registerPatient,
          indication: { value: undefined, label: "Select" },
          other_indication: null,
          clinical_trial: null,
        }));
      } else {
        setRegisterPatient(() => ({
          ...registerPatient,
          indication: capsuleCycleSelected,
          clinical_trial: null, // Set clinical_trial to empty string
          other_indication: null,
        }));
      }
    }
  };

  const commonFormProps = {
    state,
    previous,
    handleChange,
    registerPatient,
    personalDetails,
    validateFields: fields,
    updateRegisterPatient,
    errorMessage,
    showErrors,
    deletePropertyValue,
    prescriptionField,
    handleCapsules,
    checkedItems,
    checkedItemsPrescriber,
    handleCheckboxChange,
    allCheckedPrescriber,
  };

  const closeConfirmationProvided = () => {
    setOpenModal(false);
    setErrorMessage((prevState) => ({
      ...prevState,
      type: "",
      modalState: 1,
      hasSecondState: false,
      title: "",
      description: "",
      buttonText: "",
      code: "",
    }));
  };

  const continueConfirmationNotProvided = () => {
    if (errorMessage.title === "Continue with off-label use") {
      setRegisterPatient((prevState) => ({
        ...prevState,
        continueOffLabel: true,
      }));
    }

    if (errorMessage.title === errorMessages.INDICATION_INCOMPLETE_TITLE) {
      setRegisterPatient((prevState) => ({
        ...prevState,
        continueIndicationIncomplete: true,
      }));
    }

    if (errorMessage.title === errorMessages.CONFIRMATION_NOT_PROVIDED_TITLE) {
      setRegisterPatient((prevState) => ({
        ...prevState,
        continueWithoutConfirmation: true,
      }));
    }

    if (errorMessage.title === errorMessages.OUTDATED_PREGANACY_TEST_TITLE) {
      setRegisterPatient((prevState) => ({
        ...prevState,
        continueWithPregnancyTestDateOutdated: true,
      }));
    }

    if (
      errorMessage.title === errorMessages.PREGNANACY_TEST_DATE_INCOMPLETE_TITLE
    ) {
      setRegisterPatient((prevState) => ({
        ...prevState,
        continueWithPregnancyTestDateIncomplete: true,
      }));
    }

    if (errorMessage.title === errorMessages.OUTDATED_DATE_BIRTH_TITLE) {
      setRegisterPatient((prevState) => ({
        ...prevState,
        continueWithDateOfBirthUnderEighteen: true,
      }));
    }

    if (
      errorMessage.title === errorMessages.MAXIM_NUMBER_OF_CYLES_EXCEEDED_TITLE
    ) {
      setRegisterPatient((prevState) => ({
        ...prevState,
        continueMaximumNumberOfCyclesExceeded: true,
      }));
    }

    // prefer code-based checks (more reliable), fall back to title
    if (errorMessage.code === "MAX_WEEKS_EXCEEDED" || errorMessage.title === "Maximum number of weeks exceeded") {
      setRegisterPatient((prevState) => ({
        ...prevState,
        continueMaximumNumberOfWeeksExceeded: true,
      }));
    }

    if (errorMessage.code === "MAX_CAPSULES_EXCEEDED") {
      setRegisterPatient((prevState) => ({
        ...prevState,
        continueMaximumNumberOfCapsulesExceeded: true,
      }));
    }

    if (
      errorMessage.code === errorMessages.CAPSULES_PER_CYCLE_INCOMPLETE_CODE
    ) {
      setRegisterPatient((prevState) => ({
        ...prevState,
        continueCapsulePerCycleIncomplete: true,
      }));
    }

    if (
      errorMessage.code === errorMessages.CAPSULES_PER_WEEKS_INCOMPLETE_CODE
    ) {
      setRegisterPatient((prevState) => ({
        ...prevState,
        continueCapsulePerWeekIncomplete: true,
      }));
    }

    if (errorMessage.title === errorMessages.NUMBER_OF_CYCLES_INCOMPLETE) {
      setRegisterPatient((prevState) => ({
        ...prevState,
        continueNumberOfCyclesIncomplete: true,
      }));
    }
    if (errorMessage.title === errorMessages.NUMBER_OF_WEEKS_INCOMPLETE) {
      setRegisterPatient((prevState) => ({
        ...prevState,
        continueNumberOfWeeksIncomplete: true,
      }));
    }

    setOpenModal(false);
    setErrorMessage((prevState) => ({
      ...prevState,
      type: "",
      modalState: 1,
      hasSecondState: false,
      title: "",
      description: "",
      buttonText: "",
    }));
  };

  // END VALIDED WOMAN OF CHILDBEARING POTENTIAL FORM
  return (
    <div
      className="w-full min-h-screen overflow-y-auto pt-10 pb-10"
      data-testid="patient-initiation-page"
    >
      <div className="mx-auto w-full max-w-300">
        <div className="mx-2 relative z-2 rounded-[10px] bg-white px-16 py-12 shadow-[0_0_40px_rgba(0,0,0,0.2)]">
          <div
            className="absolute top-3.75 right-3.75 cursor-pointer rounded-full bg-[#9ABDB8] p-0.75"
            onClick={closeForm}
            data-testid="patient-initiation-close-button"
            role="button"
          >
            <img
              src={CloseIcon}
              alt="Close Icon"
              className="mt-0 block w-5 brightness-0 invert"
            />
          </div>
          {state <= 5 && (
            <div
              className="text-lg text-[#00567a]"
              data-testid="patient-initiation-step-label"
            >
              Step {state}/
              {registerPatient.type === typeOfPAF.WOMANOFCP ? "6" : "5"}
            </div>
          )}
          {state === 1 && <ConfirmIdentity personalDetails={personalDetails} />}
          {state === 3 && (isPrescriber || isConnectedRegistrar) && (
            <SelectTIF
              registerPatient={registerPatient}
              handleCheckboxChange={handleCheckboxChange}
              showErrors={showErrors}
              brochures={brochures}
              treatmentFormText={treatmentFormText}
            />
          )}
          {state === 4 && (
            <PatientCategory
              registerPatient={registerPatient}
              selectTypeOfPatient={selectTypeOfPatient}
            />
          )}
          {state === 2 && (
            <DrugToPrescribe
              registerPatient={registerPatient}
              selectTypeOfDrug={selectTypeOfDrug}
              drugToPrescribe={drugToPrescribe}
            />
          )}
          {state === 5 && (isPrescriber || isConnectedRegistrar) && (
            <DigitalPAF {...commonFormProps} storageData={storageData} />
          )}
          {state === 6 && (
            <React.Fragment>
              <div
                className="flex items-center justify-between"
                data-testid="patient-initiation-review-section"
              >
                <h1 data-testid="patient-initiation-review-title">
                  Prescription Authorisation Form
                </h1>
                <PatientInitiationTopButtons
                  auxButtons
                  isSubmittedPresciberPAF={isSubmittedPresciberPAF}
                  onPrevious={previous}
                  onCompleteClick={submitPAFToServer}
                  disableCompleteButton={disableCompleteButton}
                  onPrint={printPAF}
                  onFinish={redirectToOrderPage}
                />
              </div>

              <PostCreatePAFReview
                form={removeEmptyValueAndSelectLabel({ ...registerPatient })}
                patientDetails={getUserInfo()}
                prescriptionField={prescriptionField}
                storageData={storageData}
                showErrors={showErrors}
                riskCheckboxValues={checkedItems}
                prescriberCheckboxValues={checkedItemsPrescriber}
              />

              <div className="mt-5 flex">
                <div className="ml-auto">
                  <PatientInitiationTopButtons
                    isSubmittedPresciberPAF={isSubmittedPresciberPAF}
                    onPrevious={previous}
                    onCompleteClick={submitPAFToServer}
                    disableCompleteButton={disableCompleteButton}
                    onPrint={printPAF}
                    onFinish={redirectToOrderPage}
                  />
                </div>
              </div>
            </React.Fragment>
          )}
          {state <= 5 && (
            <div
              className={state === 1 ? "flex items-center justify-between" : ""}
              data-testid="patient-initiation-nav-actions"
            >
              {state === 1 && (
                <div className="flex items-center justify-between text-base italic text-[#fb3449]">
                  {/* Editable from profile */}
                </div>
              )}
              <div
                className={
                  state === 1
                    ? "flex items-center justify-end"
                    : "flex items-center justify-between pt-15"
                }
              >
                {state === 1 ? (
                  <button
                    onClick={goToProfile}
                    className="text-[#252525] hover:text-neutral-500"
                    data-testid="patient-initiation-go-to-profile-button"
                  >
                    Go to Profile
                  </button>
                ) : (
                  <button
                    onClick={previous}
                    className="text-[#252525] hover:text-neutral-500"
                    data-testid="patient-initiation-previous-button"
                  >
                    Previous
                  </button>
                )}

                <div className="flex items-center justify-between">
                  {(state === 3 || state === 5) &&
                    (isPrescriber || isConnectedRegistrar) && (
                      <div
                        className="text-base text-[#252525]"
                        data-testid="patient-initiation-mandatory-field-note"
                      >
                        *symbol indicating a mandatory field
                      </div>
                    )}
                  {(state === 1 || state === 3) && isPharmacy && (
                    <div
                      className="text-base text-[#252525]"
                      data-testid="patient-initiation-mandatory-field-note"
                    >
                      *symbol indicating a mandatory field
                    </div>
                  )}

                  <button
                    className="ml-2.5 inline-flex w-33.5 items-center justify-center rounded-full bg-[#00567a] px-5 py-2 font-semibold text-white hover:bg-[#005eb8] disabled:opacity-60"
                    onClick={nextPage}
                    disabled={isSubmitting || disableNextButton()}
                    data-testid={
                      state === 1
                        ? "patient-initiation-begin-button"
                        : "patient-initiation-next-button"
                    }
                  >
                    {state === 1 ? "Begin" : "Next"}
                    {isSubmitting && (
                      <i className="w-6.75">
                        <Loader />
                      </i>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={registerModalIsOpen}
        style={patientInitiationRegisterModalStyles}
        contentLabel="Register Modal"
        shouldCloseOnOverlayClick={false} // add this to prevent outside click to prevent modal close
      >
        <div
          className="px-8 pt-8 text-center"
          data-testid="patient-initiation-register-modal"
        >
          <div
            className="mb-3 text-2xl font-semibold text-[#00567a]"
            data-testid="patient-initiation-register-modal-title"
          >
            Prescription Authorisation Form Completed
          </div>
          <div
            className="text-base text-neutral-700"
            data-testid="patient-initiation-register-modal-description"
          >
            A copy of the prescription authorisation form will be available to
            the hospital inpatient pharmacy.
          </div>
        </div>
        <div
          className="mt-8 mb-6 flex items-center justify-center gap-4"
          data-testid="patient-initiation-register-modal-actions"
        >
          <button
            className="rounded-full border border-neutral-400 bg-white px-6 py-2 text-neutral-700 hover:bg-neutral-50"
            onClick={closeModalRegisterModal}
            data-testid="patient-initiation-register-modal-cancel-button"
          >
            Cancel
          </button>
          <button
            className="rounded-full bg-[#00567a] px-6 py-2 font-semibold text-white hover:bg-[#005eb8] disabled:opacity-60"
            onClick={submitForm}
            disabled={isSubmitting}
            data-testid="patient-initiation-register-modal-complete-button"
          >
            Complete
          </button>
        </div>
      </Modal>

      {quitModalIsOpen && (
        <QuitModal
          quitModalIsOpen={quitModalIsOpen}
          closeModalQuit={closeModalQuit}
          quitModal={quitModal}
          description={`Are you sure you want to cancel filling out this ${treatmentFormLabel}. All progress will be lost and you will need to start again.`}
        />
      )}

      <ContinueConfirmationProvidedModal
        errorMessage={errorMessage}
        openModal={openModal}
        closeConfirmationProvided={closeConfirmationProvided}
        continueConfirmationNotProvided={continueConfirmationNotProvided}
      />

      <NonComplaintPAF
        openModalNonComplaint={openModalNonComplaint}
        closeNonComplaint={closeNonComplaint}
        submitNonComplaint={submitNonComplaint}
        handleChangeValidPassword={handleChangeValidPassword}
        registerPatient={registerPatient}
        displayWeeks={scheduleDisplayType}
        errorMessageFromServer={errorMessageFromServer}
        hcp={hcp}
      />
    </div >
  );
};
export default PatientInitiation;
